import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import SignInForm from '../components/event/SignInForm'
import ScheduleSection from '../components/event/ScheduleSection'
import TeamReveal from '../components/event/TeamReveal'
import { getParticipantState } from '../lib/api'
import { useSession } from '../lib/useSession'
import type { ParticipantView } from '../event/state'

/** How often the space checks for changes. */
const POLL_MS = 10_000

function ParticipantSpace({ email }: { email: string }) {
  const [view, setView] = useState<ParticipantView | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    async function load() {
      try {
        const next = await getParticipantState()
        if (cancelled) return
        setView(next)
        setError(null)
        // Keeps checking all day: the reveal, the assignment, corrections, and results.
        timer = setTimeout(load, POLL_MS)
      } catch {
        if (cancelled) return
        setError('The event state could not be loaded. Retrying.')
        timer = setTimeout(load, POLL_MS)
      }
    }
    void load()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [])

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">
            {view?.name ? `Welcome, ${view.name.split(' ')[0]}` : 'Participant space'}
          </h1>
          <p className="mt-1 text-[var(--page-ink-muted)]">Signed in as {email}</p>
        </div>
      </div>

      <section aria-labelledby="team-heading">
        <h2 id="team-heading" className="mb-4 text-2xl font-black tracking-tight">
          Your team
        </h2>
        {error && <p className="mb-3 text-[var(--page-danger)]">{error}</p>}
        {view ? <TeamReveal view={view} /> : <p>Loading your team.</p>}
      </section>

      <ScheduleSection />


      {view && view.results.length > 0 && (
        <section aria-labelledby="results-heading">
          <h2 id="results-heading" className="text-2xl font-black tracking-tight">
            Results
          </h2>
          <ul className="mt-3 list-disc pl-6">
            {view.results.map((r) => (
              <li key={r.id}>
                <a href={r.url} className="font-semibold text-[var(--page-accent-text)] underline underline-offset-4">
                  {r.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

export default function ParticipantPage() {
  const { state, signedIn } = useSession()

  return (
    <div className="page-shell py-10">
      {state.status === 'checking' && <p>Checking your sign-in.</p>}
      {state.status === 'signed-out' && (
        <>
          {state.error && <p className="mb-4 text-[var(--page-danger)]">{state.error}</p>}
          <SignInForm role="participant" onSignedIn={signedIn} />
        </>
      )}
      {state.status === 'signed-in' && state.session.role === 'admin' && <Navigate to="/admin" replace />}
      {state.status === 'signed-in' && state.session.role === 'participant' && (
        <ParticipantSpace email={state.session.email} />
      )}
    </div>
  )
}
